import { Head } from "@inertiajs/react"
import { Sparkles } from "lucide-react"
import { useTranslation } from "react-i18next"

import { FeedPostCard } from "@/components/crew/feed-post-card"
import { FeedStatsBand } from "@/components/crew/feed-stats-band"
import { EmptyState } from "@/components/empty-state"
import AppLayout from "@/layouts/app-layout"
import type { CreatorFeedProps } from "@/types/feed"

/**
 * Creator/Feed/Index — §10 Crew Feed (OKS-509).
 *
 * The living TikTok-embed feed: a crew-campaign stats band atop a single
 * column of crewmate posts. Each post embeds the TikTok player (client-side,
 * TRD KD-1), shows the member identity + on-post counts, and offers ❤ "Like &
 * comment on TikTok" (deep-link, real engagement) + 🔥 cheer (optimistic peer
 * recognition). Lives inside `AppLayout` (creator-shell).
 *
 * States (UX Spec §3):
 *   - empty   — 0 posts → stats band (zeros) + `EmptyState` "be the first".
 *   - loading — client-nav skeleton (`FeedSkeleton`); per-card embed skeletons
 *               cover the iframe-resolving state on the populated feed.
 *   - error   — a cross-brand / nil-brand context yields an EMPTY feed
 *               server-side (fail-closed), never an error screen.
 *   - success — stats band + post cards + a subtle "more from your crew" peek.
 *
 * Brand isolation is enforced upstream: `CrewFeedQuery` floors every post
 * through `Mission.for_brand(Current.brand)` — a Brand B creator never sees
 * Brand A posts, counts, or cheers.
 */
export default function CreatorFeedIndex({ posts, stats }: CreatorFeedProps) {
  const { t } = useTranslation()
  const isEmpty = posts.length === 0

  return (
    <AppLayout>
      <Head title={t("crew_feed.title")} />

      <main className="mx-auto flex w-full max-w-xl flex-col gap-4 py-5">
        <header className="px-4">
          <h1 className="text-2xl font-bold tracking-tight">
            {t("crew_feed.title")}
          </h1>
          <p className="text-muted-foreground text-sm">
            {t("crew_feed.subtitle")}
          </p>
        </header>

        <div className="px-4">
          <FeedStatsBand stats={stats} />
        </div>

        {isEmpty ? (
          <div className="px-4">
            <EmptyState
              icon={Sparkles}
              title={t("crew_feed.empty.title")}
              description={t("crew_feed.empty.description")}
              data-testid="feed-empty"
            />
          </div>
        ) : (
          <ul data-testid="feed-list" className="flex flex-col gap-4 px-4">
            {posts.map((post) => (
              <li key={post.id}>
                <FeedPostCard post={post} />
              </li>
            ))}

            {/* "More from your crew" peek — subtle continued-scroll affordance
                (UX Spec §2). Hidden for a single-post crew (§5 edge). */}
            {posts.length > 1 && (
              <li
                data-testid="feed-peek"
                aria-hidden="true"
                className="text-muted-foreground/70 pb-2 text-center text-xs"
              >
                {t("crew_feed.peek")}
              </li>
            )}
          </ul>
        )}
      </main>
    </AppLayout>
  )
}
